/**
 * Greek (el-GR) localization strings.
 * All UI text MUST go through this file — never hardcode Greek in JSX.
 * Keys are in English; values are in Greek with proper monotonic accents.
 */

export const t = {
  // App-wide
  appName: 'Λεξοναύτες',
  appTagline: 'Εξερεύνησε τις λέξεις!',

  // Dictionary
  dictionary: {
    pageTitle: 'Λεξικό μου',
    pageSubtitle: 'Οι λέξεις που έχεις μάθει',
    emptyTitle: 'Άδειο για την ώρα',
    emptyText: 'Παίξε λίγο για να γεμίσει το λεξικό σου με λέξεις που έχεις μάθει.',
    searchPlaceholder: 'Αναζήτηση λέξης…',
    definitionLabel: 'Σημασία',
    exampleLabel: 'Παράδειγμα',
    synonymsLabel: 'Συνώνυμα',
    familyLabel: 'Συγγενικές',
    etymologyLabel: 'Ετυμολογία',
    noDefinitionYet: 'Δεν υπάρχει σημασία ακόμη.',
    masteredCount: '{count} λέξεις μαθεμένες',
    totalCount: '{count} λέξεις στο λεξικό',
    tabAll: 'Όλες',
    tabMastered: 'Έμαθα',
    tabAllHint: 'Όλες οι λέξεις του λεξικού',
    tabMasteredHint: 'Λέξεις που έμαθες παίζοντας',
    masteredEmptyTitle: 'Δεν έχεις μάθει λέξεις ακόμη',
    masteredEmptyText: 'Παίξε λίγο και μάζεψε σωστές απαντήσεις για να γεμίσει αυτός ο φάκελος.',
    exerciseDictTitle: 'Λεξικό άσκησης',
    exerciseDictHint: 'Δες τι σημαίνουν οι λέξεις της άσκησης',
    exerciseDictEmpty: 'Δεν υπάρχουν λέξεις της άσκησης στο λεξικό ακόμη.',
    exerciseDictAria: 'Λεξικό άσκησης',
    popoverTitle: 'Σημασία λέξης',
    popoverClose: 'Κλείσιμο',
    gameTitle: 'Λέξη ↔ Σημασία',
    gameDescription: 'Διάλεξε τη σωστή λέξη που ταιριάζει με τη σημασία.',
    gameStart: 'Παίξε!',
    gameNotEnoughTitle: 'Δεν υπάρχουν αρκετές λέξεις με σημασία ακόμη',
    gameNotEnoughText:
      'Χρειαζόμαστε τουλάχιστον 4 λέξεις με σημασία στο λεξικό για να παίξουμε.',
    gameRoundPrompt: 'Ποια λέξη ταιριάζει με τη σημασία;',
    gameRoundCount: 'Γύρος {current} από {total}',
    gameCompleted: 'Τελείωσες!',
    gameScore: 'Σωστές: {correct}/{total}',
  },

  // Daily quests (home screen)
  quests: {
    todayTitle: 'Σημερινές αποστολές',
    completed: 'Ολοκληρώθηκε!',
    rewardSuffix: ' πόντοι',
    progressOf: 'από',
    // Weekend rest-day card — shown instead of the quest panel on Sat/Sun.
    restDayTitle: 'Ημέρα ξεκούρασης!',
    restDayBody:
      'Σαββατοκύριακο! Δεν υπάρχουν αποστολές σήμερα — παίξε ελεύθερα όσο θέλεις. 🌴',
    // Gentle nudge when there are missed weekdays still open for make-up.
    makeupNudge:
      'Σου έμειναν αποστολές από καθημερινές αυτής της εβδομάδας. Μπορείς να τις αναπληρώσεις!',
    makeupCta: '📅 Πάμε για αναπλήρωση',
    labels: {
      new_words_3: 'Μάθε 3 νέες λέξεις',
      new_words_5: 'Μάθε 5 νέες λέξεις',
      new_words_10: 'Μάθε 10 νέες λέξεις',
      correct_first_5: '5 σωστές απαντήσεις με την πρώτη',
      correct_first_8: '8 σωστές απαντήσεις με την πρώτη',
      correct_first_15: '15 σωστές απαντήσεις με την πρώτη',
      perfect_session_1: 'Κάνε μία τέλεια εξάσκηση',
      // Dynamic quest — `{n}` is replaced with the actual target. The
      // singular/plural distinction matters in Greek so we keep both forms
      // available and let the renderer pick the right one based on n.
      mistakes_cleared_dynamic: 'Διόρθωσε {n} λάθος {wordForm}',
      // Legacy keys kept so any stored quest from before the dynamic
      // refactor still renders a readable label until day-rollover.
      mistakes_cleared_3: 'Διόρθωσε 3 λάθος λέξεις',
      mistakes_cleared_5: 'Διόρθωσε 5 λάθος λέξεις',
      sessions_played_1: 'Παίξε μία άσκηση',
      sessions_played_3: 'Παίξε 3 ασκήσεις',
      // Weekly review — Friday's only daily mission (replaces the normal set).
      // The label is intentionally distinct so the child notices it's special.
      weekly_review_mandatory: 'Επανάληψη της εβδομάδας — ξαναπαίξε λέξεις',
    },
    // Greek word forms for interpolated quest labels. Add more pairs here
    // when other dynamic quests need plural-aware copy.
    wordForms: {
      singularLexi: 'λέξη',
      pluralLexi: 'λέξεις',
    },
  },

  // Λεξοδάσος (forest) page
  forest: {
    pageTitle: 'Το λεξοδάσος μου',
    pageSubtitle: 'Κάθε δέντρο αντιπροσωπεύει ένα είδος ορθογραφίας — μεγάλωσέ τα όλα!',
    lockedTitle: 'Κλειδωμένο',
    lockedBody:
      'Το λεξοδάσος ξεκλειδώνει όταν το λεξοδέντρο φτάσει στο τελευταίο στάδιο. Συνέχισε την εξάσκηση!',
    backToHome: 'Πίσω στην αρχική',
    legendTitle: 'Πώς μεγαλώνουν τα δέντρα;',
    legendMastery: '+10 όταν διορθώνεις ένα λάθος του είδους του',
    legendDrip: '+1 για κάθε σωστή απάντηση με την πρώτη',
    legendQuest: '+5 ισομοιρασμένα με κάθε ημερήσια αποστολή',
    notEncountered: 'Δεν έχεις συναντήσει ακόμα ασκήσεις αυτού του είδους',
    growthLabel: 'Πρόοδος',
    speciesCounter: 'Φυτεμένα: {grown} από {total}',
    // Common names of each species, indexed by the `nameKey` in
    // `src/data/forestSpecies.jsx`.
    species: {
      cherry: 'Κερασιά',
      olive: 'Ελιά',
      oak: 'Δρυς',
      willow: 'Ιτιά',
      apple: 'Μηλιά',
      lemon: 'Λεμονιά',
      fig: 'Συκιά',
      pine: 'Πεύκο',
      cypress: 'Κυπαρίσσι',
      palm: 'Φοίνικας',
      plane: 'Πλάτανος',
    },
    // Per-tag concrete tip — shown when the child taps a tree (or below the
    // tree by default). Keys are canonical error_tags.
    tips: {
      tonismos: 'Παίξε εξασκήσεις τονισμού για να ανθίσει η κερασιά σου.',
      ei_i_oi:
        'Δούλεψε με λέξεις που μπερδεύεις ει/η/οι για να μεγαλώσει η ελιά.',
      dipla_sygfona:
        'Εστίασε στα διπλά σύμφωνα (γγ, νν, λλ) για να ψηλώσει η δρυς.',
      afonos_h: 'Πρόσεξε το άφωνο η στο τέλος των λέξεων — η ιτιά σε θυμάται.',
      omikron_omega:
        'Ξεχώρισε ο και ω: όσο πιο σωστά γράφεις, τόσο πιο φουντωμένη η μηλιά.',
      epsilon_aita:
        'Διάκριση ε και αι: η λεμονιά σου ωριμάζει με κάθε σωστή απάντηση.',
      upsilon_oi: 'Δούλεψε υ και οι για να γεμίσει η συκιά με σύκα.',
      ou_oi_diphthong:
        'Ακούς /ου/ ή /οι/; Σωστή απάντηση = ένας ακόμα κώνος για το πεύκο.',
      gender_agreement:
        'Πρόσεξε το γένος του ουσιαστικού — το κυπαρίσσι σου ψηλώνει.',
      verb_ending:
        'Δούλεψε τις καταλήξεις ρημάτων για να ξεδιπλώσει ο φοίνικας τα φύλλα του.',
      compound_words:
        'Σύνθετες λέξεις: ο πλάτανος απλώνει τη σκιά του όταν τις γράφεις σωστά.',
    },
  },

  // Pet widget (home screen)
  pet: {
    widgetTitle: 'Λεξοδέντρο',
    stages: {
      seed: 'Σπόρος',
      sprout: 'Βλαστάρι',
      sapling: 'Φυντανάκι',
      youngTree: 'Νεαρό δέντρο',
      lexoTree: 'Λεξοδέντρο',
      bloomingTree: 'Ανθισμένο Λεξοδέντρο',
      fruitfulTree: 'Καρπερό Λεξοδέντρο',
      nestedTree: 'Φωλιασμένο Λεξοδέντρο',
      ancientTree: 'Γέρικο Λεξοδέντρο',
      legendaryTree: 'Θρυλικό Λεξοδέντρο',
      worldTree: 'Λεξοδέντρο της Γνώσης',
    },
    growthToNext: 'για το επόμενο στάδιο',
    fullyGrown: 'Έχει μεγαλώσει εντελώς!',
    forestUnlockCta: 'Άνοιξε το λεξοδάσος μου',
    forestUnlockHint: 'Το λεξοδέντρο σου έφτασε στο τελευταίο στάδιο — άνοιξε το δάσος για να ξεκινήσεις νέα είδη!',
    forestLockedHint: 'Φτάσε το λεξοδέντρο στο τελευταίο στάδιο για να ξεκλειδώσεις το λεξοδάσος.',
  },

  // Avatar picker
  avatar: {
    pageTitle: 'Στόλισε τη μασκότ σου',
    sectionHat: 'Καπέλα',
    locked: 'Κλειδωμένο',
    unlockHint: 'Ξεκλείδωσε στα {xp} πόντους',
    unlockHintWithBadges: 'Ξεκλείδωσε στα {xp} πόντους + παράσημα',
    equipped: 'Φοριέται',
    equipCta: 'Φόρεσέ το',
    detail: {
      lockedTitle: 'Πώς θα το ξεκλειδώσεις',
      openLockedAria: 'Δες πώς ξεκλειδώνεις το {label}',
      xpSectionTitle: 'Πόντοι εμπειρίας',
      xpProgressTemplate: '{current} / {target}',
      xpRemainingTemplate: 'Σου λείπουν ακόμα {remaining} πόντοι',
      xpReadyTemplate: 'Έχεις ήδη τους πόντους που χρειάζεσαι!',
      badgesSectionTitle: 'Παράσημα που χρειάζεσαι',
      badgeEarnedAria: 'Κερδισμένο',
      badgeLockedAria: 'Δεν το έχεις ακόμα',
      unknownBadge: 'Άγνωστο παράσημο',
    },
    items: {
      noHat: 'Χωρίς καπέλο',
      sunHat: 'Ψάθινο καπέλο',
      spaceHelmet: 'Διαστημικό κράνος',
      captainCap: 'Καπέλο καπετάνιου',
      wizardCap: 'Μαγικό καπέλο',
      crown: 'Κορώνα',
      dinosaurCap: 'Καπέλο δεινοσαύρου',
      pirateHat: 'Πειρατικό καπέλο',
      knightHelmet: 'Κράνος ιππότη',
      archerCap: 'Καπέλο τοξότη',
      detectiveCap: 'Καπέλο ντετέκτιβ',
      pharaohHeaddress: 'Κουκούλα φαραώ',
      robotHelmet: 'Κράνος ρομπότ',
      olympicLaurel: 'Στέφανος δάφνης',
      cosmicCaptainCap: 'Καπέλο διαστημόπλοιου',
      diademOfKnowledge: 'Διάδημα γνώσης',
      treasureChestHat: 'Καπέλο θησαυρού',
      timeTravelerHelm: 'Κράνος χρονοταξιδιώτη',
    },
  },

  // PWA install prompt (home screen banner)
  pwa: {
    installTitle: 'Πρόσθεσε στην αρχική!',
    installText: 'Βάλε τους Λεξοναύτες σαν εφαρμογή στο κινητό σου για να παίζεις πιο εύκολα.',
    installCta: '📲 Εγκατάσταση',
    installDismiss: 'Όχι τώρα',
    installSuccessTitle: 'Έτοιμο!',
    installSuccessText: 'Οι Λεξοναύτες μπήκαν στην αρχική σου οθόνη.',
  },

  // Online sync layer — header indicator + sign-in badge + save-progress banner.
  sync: {
    iconLabel: 'Κατάσταση αποθήκευσης',
    statusSyncing: 'Αποθήκευση…',
    statusSynced: 'Αποθηκευμένο online',
    statusError: 'Δεν αποθηκεύτηκε — θα ξαναπροσπαθήσει',
    statusOffline: 'Μόνο σε αυτή τη συσκευή',
    signedInLabel: 'Συνδεδεμένος — η πρόοδός σου φυλάσσεται',
    // Gentle "back up your progress" banner (Progress page).
    saveTitle: 'Μην χάσεις την πρόοδό σου!',
    saveText: 'Σύνδεσε έναν λογαριασμό για να κρατήσεις τα αστέρια και τα παράσημά σου με ασφάλεια, σε κάθε συσκευή.',
    saveCta: 'Σύνδεση',
    saveDismiss: 'Όχι τώρα',
    accountCta: 'Λογαριασμός',
  },

  // Parent account / sign-in screen (/account).
  account: {
    title: 'Λογαριασμός γονέα',
    grownupNote: 'Αυτή η σελίδα είναι για έναν μεγάλο. Με τη σύνδεση, η πρόοδος του παιδιού φυλάσσεται με ασφάλεια και σε άλλη συσκευή.',
    emailLabel: 'Email',
    emailPlaceholder: 'το email σου',
    signInCta: 'Σύνδεση',
    // Passwordless e-mail-code sign-in (two steps): ask for a code, then enter it.
    passwordlessNote: 'Δεν χρειάζεται κωδικός! Γράψε το email σου και θα σου στείλουμε έναν προσωρινό κωδικό για να συνδεθείς.',
    sendCodeCta: 'Στείλε μου κωδικό',
    codeLabel: 'Κωδικός από το email',
    codePlaceholder: 'π.χ. K7M2P9QX',
    codeSentNote: 'Σου στείλαμε έναν κωδικό 8 χαρακτήρων στο email σου. Ισχύει για 10 λεπτά.',
    verifyCta: 'Σύνδεση',
    changeEmailCta: '← Άλλο email',
    resendCta: 'Στείλε ξανά',
    devSignInCta: '⚡ Γρήγορη σύνδεση (dev)',
    devSignInNote: 'Μόνο για δοκιμές — συνδέεσαι χωρίς στοιχεία.',
    working: 'Περίμενε…',
    sending: 'Στέλνουμε…',
    errorGeneric: 'Κάτι πήγε στραβά. Δοκίμασε ξανά.',
    errorEmailInvalid: 'Γράψε ένα σωστό email.',
    errorCodeInvalid: 'Ο κωδικός είναι λάθος ή έληξε. Ζήτησε έναν νέο.',
    // Signed-in panel
    signedInTitle: 'Είσαι συνδεδεμένος!',
    signedInText: 'Η πρόοδος του παιδιού αποθηκεύεται αυτόματα.',
    continueCta: 'Πάμε!',
    manageChildrenCta: '👧 Τα παιδιά μου',
    signOutCta: 'Αποσύνδεση',
  },

  // Parents / children management screen (/parents).
  parents: {
    title: 'Περιοχή γονέα',
    grownupNote: 'Αυτή η σελίδα είναι για έναν μεγάλο: εδώ φτιάχνεις προφίλ για τα παιδιά σου και διαλέγεις ποιο παίζει τώρα σε αυτή τη συσκευή.',
    childrenSection: 'Τα παιδιά μου',
    childrenSubtitle: 'Διάλεξε ποιο παιδί παίζει σε αυτή τη συσκευή — ή πρόσθεσε νέο.',
    familySection: 'Οικογένεια',
    deviceSection: 'Αυτή η συσκευή',
    signedOutTitle: 'Χρειάζεται σύνδεση',
    signedOutText: 'Σύνδεσε έναν λογαριασμό γονέα για να φτιάξεις προφίλ παιδιών και να αποθηκεύεις την πρόοδό τους.',
    signInCta: 'Σύνδεση γονέα',
    loading: 'Φόρτωση…',
    loadError: 'Δεν φορτώθηκαν τα προφίλ. Έλεγξε τη σύνδεση και δοκίμασε ξανά.',
    retry: 'Δοκίμασε ξανά',
    empty: 'Δεν υπάρχουν προφίλ ακόμα. Πρόσθεσε το πρώτο παιδί!',
    activeBadge: '▶️ Παίζει τώρα',
    playCta: 'Παίξε',
    insightsCta: '📊 Πρόοδος',
    editCta: 'Επεξεργασία',
    deleteCta: 'Διαγραφή',
    deleteConfirm: 'Να διαγραφεί αυτό το προφίλ μαζί με την πρόοδό του; Δεν αναιρείται.',
    addChildCta: '➕ Προσθήκη παιδιού',
    addTitle: 'Προσθήκη παιδιού',
    editTitle: 'Επεξεργασία παιδιού',
    nameLabel: 'Όνομα (ή ψευδώνυμο)',
    namePlaceholder: 'π.χ. Άρης',
    gradeLabel: 'Τάξη',
    addCta: 'Προσθήκη',
    saveCta: 'Αποθήκευση',
    cancelCta: 'Άκυρο',
    working: 'Περίμενε…',
    saveError: 'Κάτι πήγε στραβά. Δοκίμασε ξανά.',
    nameRequired: 'Γράψε ένα όνομα.',
    grades: { 2: 'Β΄ Δημοτικού', 3: 'Γ΄ Δημοτικού', 4: 'Δ΄ Δημοτικού' },
  },

  // Child progress dashboard (/parents/:profileId/insights) — read-only view
  // for a grown-up. Reuses `errorLabels` for weakness names.
  insights: {
    title: 'Η πρόοδος του παιδιού',
    loading: 'Φόρτωση προόδου…',
    loadError: 'Δεν φορτώθηκε η πρόοδος. Έλεγξε τη σύνδεση και δοκίμασε ξανά.',
    retry: 'Δοκίμασε ξανά',
    noProgressYet: 'Αυτό το παιδί δεν έχει ξεκινήσει εξασκήσεις ακόμη.',
    stats: {
      xp: 'Πόντοι',
      currentStreak: 'Σειρά ημερών',
      bestStreak: 'Καλύτερη σειρά',
      sessions: 'Εξασκήσεις',
      perfect: 'Τέλειες (10/10)',
      badges: 'Παράσημα',
    },
    activityTitle: 'Δραστηριότητα (30 ημέρες)',
    activityEmpty: 'Καμία εξάσκηση τις τελευταίες 30 ημέρες.',
    activitySummary: '{sessions} εξασκήσεις · ακρίβεια {accuracy}%',
    activityAria: 'Δραστηριότητα ανά ημέρα, τελευταίες 30 ημέρες',
    dayAria: '{date}: {sessions} εξασκήσεις',
    rangeStartAria: 'Πριν 30 ημέρες',
    today: 'Σήμερα',
    masteryTitle: 'Λέξεις',
    mastered: 'Μαθεμένες',
    inMistakes: 'Σε λάθη',
    tracked: 'Σε εξάσκηση',
    weaknessesTitle: 'Σημεία προς βελτίωση',
    weaknessesEmpty: 'Καμία αδυναμία ακόμη — μπράβο!',
  },

  // Co-parent (household sharing) — inside the parents screen.
  family: {
    sectionTitle: 'Άλλος γονέας',
    sectionNote: 'Δώσε πρόσβαση και στον άλλο γονέα στα ίδια παιδιά.',
    membersTitle: 'Μέλη',
    you: '(εσύ)',
    roleOwner: 'Δημιουργός',
    roleMember: 'Γονέας',
    inviteCta: '➕ Πρόσκληση γονέα',
    inviteGenerating: 'Δημιουργία…',
    inviteCodeLabel: 'Κωδικός πρόσκλησης',
    inviteHelp: 'Στείλε αυτόν τον κωδικό στον άλλο γονέα. Λήγει σε 7 ημέρες και χρησιμοποιείται μία φορά.',
    copyCta: 'Αντιγραφή',
    copied: '✓ Αντιγράφηκε!',
    haveCodeCta: 'Έχω κωδικό πρόσκλησης',
    joinLabel: 'Κωδικός πρόσκλησης',
    joinPlaceholder: 'π.χ. ABCD-EFGH-JKLM',
    joinCta: 'Σύνδεση στην οικογένεια',
    joinSuccess: '✓ Συνδέθηκες! Τώρα βλέπεις τα ίδια παιδιά.',
    errorInvalidCode: 'Ο κωδικός δεν είναι σωστός ή έχει λήξει.',
    errorHouseholdNotEmpty: 'Έχεις ήδη παιδιά σε αυτόν τον λογαριασμό, οπότε δεν γίνεται σύνδεση σε άλλη οικογένεια.',
    errorGeneric: 'Κάτι πήγε στραβά. Δοκίμασε ξανά.',
  },

  // Parent-area gate (email one-time code) — shown when a child device's parent
  // area is locked. Reuses the passwordless sign-in machinery server-side.
  parentGate: {
    title: 'Μόνο για μεγάλους',
    intro: 'Για να μπεις στην περιοχή γονέα, στέλνουμε έναν κωδικό στο email ενός γονέα. Γράψε τον κωδικό εδώ.',
    chooseMemberLabel: 'Στείλε τον κωδικό στο:',
    sendCta: 'Στείλε κωδικό',
    sending: 'Αποστολή…',
    sentTo: 'Στείλαμε κωδικό στο {email}. Δες τα εισερχόμενά σου.',
    codeLabel: 'Κωδικός',
    codePlaceholder: 'π.χ. ABCD2345',
    verifyCta: 'Επιβεβαίωση',
    verifying: 'Έλεγχος…',
    resendCta: 'Στείλε ξανά',
    changeRecipientCta: 'Αλλαγή παραλήπτη',
    backCta: '← Πίσω στο παιχνίδι',
    errorSend: 'Δεν στάλθηκε ο κωδικός. Δοκίμασε ξανά.',
    errorVerify: 'Ο κωδικός δεν είναι σωστός ή έχει λήξει.',
    noMembers: 'Δεν βρέθηκε email γονέα σε αυτόν τον λογαριασμό.',
    loadError: 'Κάτι πήγε στραβά. Δοκίμασε ξανά.',
    loading: 'Φόρτωση…',
    devCodeNote: 'Κωδικός δοκιμής: {code}',
  },

  // Device settings (inside the parent area) — role + default gate recipient.
  deviceSettings: {
    title: 'Ρυθμίσεις συσκευής',
    roleLabel: 'Τι είναι αυτή η συσκευή;',
    roleChild: 'Συσκευή παιδιού',
    roleChildHint: 'Ανοίγει στο παιχνίδι. Η περιοχή γονέα ζητά κωδικό κάθε 15 λεπτά.',
    roleParent: 'Συσκευή γονέα',
    roleParentHint: 'Ανοίγει στην περιοχή γονέα και μένει ξεκλείδωτη όσο είσαι συνδεδεμένος.',
    defaultRecipientLabel: 'Προεπιλεγμένο email για τον κωδικό',
    defaultRecipientHint: 'Εκεί θα στέλνεται ο κωδικός της πύλης από προεπιλογή.',
    noDefault: 'Καμία προεπιλογή ακόμη',
    saved: '✓ Αποθηκεύτηκε',
  },

  // Child-profile deletion: irreversible, so a one-time code is mailed to a
  // household member and must be entered to confirm.
  deleteFlow: {
    title: 'Διαγραφή προφίλ',
    childLine: 'Παιδί: {name}',
    recipientLabel: 'Στείλε κωδικό επιβεβαίωσης στο:',
    sendCta: 'Στείλε κωδικό',
    sending: 'Αποστολή…',
    sentTo: 'Στείλαμε κωδικό στο {email}. Δες τα εισερχόμενά σου.',
    codeLabel: 'Κωδικός επιβεβαίωσης',
    codePlaceholder: 'π.χ. ABCD2345',
    confirmCta: '🗑️ Οριστική διαγραφή',
    deleting: 'Διαγραφή…',
    resendCta: 'Στείλε ξανά',
    changeRecipientCta: 'Αλλαγή παραλήπτη',
    cancelCta: 'Άκυρο',
    errorSend: 'Δεν στάλθηκε ο κωδικός. Δοκίμασε ξανά.',
    errorVerify: 'Ο κωδικός δεν είναι σωστός ή έχει λήξει.',
    noMembers: 'Δεν βρέθηκε email γονέα σε αυτόν τον λογαριασμό.',
    loadError: 'Κάτι πήγε στραβά. Δοκίμασε ξανά.',
    loading: 'Φόρτωση…',
    devCodeNote: 'Κωδικός δοκιμής: {code}',
  },

  // First-run sign-in gateway (/welcome) — parent sign-in OR play-as-guest.
  signin: {
    welcomeTitle: 'Καλώς ήρθες στους Λεξοναύτες!',
    welcomeSubtitle: 'Σύνδεσε έναν λογαριασμό για να φυλάς την πρόοδο σε κάθε συσκευή — ή ξεκίνα να παίζεις αμέσως.',
    parentSectionTitle: 'Για γονείς — σύνδεση',
    orDivider: 'ή',
    // Google
    googleCta: 'Συνέχεια με Google',
    googleUnavailable: 'Η σύνδεση με Google θα ενεργοποιηθεί σύντομα.',
    googleError: 'Η σύνδεση με Google δεν ολοκληρώθηκε. Δοκίμασε ξανά.',
    // Guest bypass
    guestTitle: 'Θέλω να παίξω τώρα',
    guestCta: '▶️ Παίξε χωρίς λογαριασμό',
    guestNote: 'Η πρόοδος μένει μόνο σε αυτή τη συσκευή. Ένας γονέας μπορεί να συνδεθεί αργότερα για να την κρατήσει με ασφάλεια.',
  },

  // Theme picker (home screen)
  theme: {
    pickerLabel: 'Διάλεξε εμφάνιση',
    pickerHelp: 'Άλλαξε όποτε θες!',
    fabAria: 'Άλλαξε εμφάνιση',
    popoverTitle: 'Διάλεξε εμφάνιση',
    popoverClose: 'Κλείσιμο',
    variants: {
      classic: 'Κλασικό',
      cosmic: 'Διαστημικό',
      summer: 'Καλοκαίρι',
      dino: 'Δεινόσαυροι',
      minoan: 'Μινωικός πολιτισμός',
    },
    variantDescriptions: {
      classic: 'Ζωηρά παστέλ χρώματα',
      cosmic: 'Αστέρια και ρουκέτες',
      summer: 'Θάλασσα και ήλιος',
      dino: 'Ζούγκλα με δεινόσαυρους',
      minoan: 'Παλάτι Κνωσσού και δελφίνια',
    },
    layoutSectionTitle: 'Διάταξη αρχικής',
    layouts: {
      stack: 'Στοίβα',
      dashboard: 'Πλέγμα 2×2',
    },
    layoutDescriptions: {
      stack: 'Όλα τα πλαίσια το ένα κάτω από το άλλο',
      dashboard: 'Συμπαγή πλακάκια — λιγότερο scroll',
    },
  },

  // Home layout: compact tiles labels (dashboard mode)
  homeTiles: {
    questsTitle: 'Σημερινές αποστολές',
    petTitle: 'Λεξοδέντρο',
    avatarTitle: 'Στολή μασκότ',
    avatarChange: 'Άλλαξε στολή',
    avatarOpenAria: 'Άνοιξε τη συλλογή στολών',
    adviceTitle: 'Συμβουλή',
  },

  // Navigation / common actions
  nav: {
    home: 'Αρχική',
    progress: 'Πρόοδος',
    back: 'Πίσω',
    next: 'Επόμενο',
    continue: 'Συνέχεια',
    submit: 'Απάντησε',
    skip: 'Παράλειψη',
    retry: 'Ξανά',
    finish: 'Τέλος',
    start: 'Ξεκίνα',
    close: 'Κλείσιμο',
  },

  // Home page
  home: {
    welcome: 'Καλώς ήρθες!',
    chooseLevel: 'Διάλεξε τάξη',
    lastPlayedAria: 'Τελευταία τάξη που έπαιξες',
    grade2: 'Β΄ Δημοτικού',
    grade3: 'Γ΄ Δημοτικού',
    grade4: 'Δ΄ Δημοτικού',
    grade2Description: 'Απλές λέξεις',
    grade3Description: 'Μεσαία δυσκολία',
    grade4Description: 'Πιο σύνθετες λέξεις',
    startSession: 'Ξεκίνα εξάσκηση',
    viewProgress: 'Δες την πρόοδό σου',
    tipOfTheDayLabel: 'Συμβουλή της ημέρας',
    suggestionLabel: 'Πρόταση για εσένα',
    suggestionMistakes:
      'Έχεις {count} λέξεις που χρειάζονται επανάληψη. Δοκίμασε το παιχνίδι «Επανάληψη Λαθών»!',
    suggestionWeakness:
      'Έχεις δυσκολίες στο: {label}. Παίξε «Γραμματικοί Κανόνες» για να εξασκηθείς.',
    suggestionContinue:
      'Πάμε για άλλο γύρο! Παίξε το κουίζ για να μαζέψεις πόντους.',
    suggestionFirstTime:
      'Καλώς ήρθες! Διάλεξε τάξη και ξεκίνα με το κουίζ — 10 ερωτήσεις, εύκολο και διασκεδαστικό.',
    suggestionActionPlayQuiz: 'Παίξε κουίζ',
    suggestionActionMistakes: 'Επανάληψη Λαθών',
    suggestionActionGrammar: 'Δες κανόνες',
  },

  // Play selection page (grade → game)
  play: {
    chooseGame: 'Διάλεξε παιχνίδι',
    quizTitle: '10 Ερωτήσεις',
    quizDescription: 'Λέξεις και προτάσεις με 3 προσπάθειες ανά ερώτηση και αξιολόγηση στο τέλος.',
    quizStart: 'Παίξε!',
    grammarTitle: 'Γραμματικοί Κανόνες',
    grammarDescription: 'Διάλεξε έναν κανόνα, μάθε τον και κάνε ασκήσεις για να τον εμπεδώσεις.',
    grammarStart: 'Δες κανόνες',
    mistakesTitle: 'Επανάληψη Λαθών',
    mistakesDescription: 'Εξάσκηση μόνο σε λέξεις που έχεις κάνει λάθος, για να τις μάθεις καλά.',
    mistakesStart: 'Ξεκίνα επανάληψη',
    mistakesEmptyTitle: 'Καμία λέξη για επανάληψη!',
    mistakesEmptyText: 'Δεν έχεις λάθη να εξασκηθείς ακόμη. Παίξε το κανονικό κουίζ για να μαζέψεις λέξεις προς εξάσκηση.',
    historyTitle: 'Επανάληψη Όλων',
    historyDescription:
      'Ξαναπαίξε λέξεις που έχεις ήδη εξασκηθεί. Έως 30 λέξεις από το ιστορικό σου.',
    historyStart: 'Ξεκίνα επανάληψη',
    dictionaryGameTitle: 'Λέξη ↔ Σημασία',
    dictionaryGameDescription: 'Διάβασε μια σημασία και βρες ποια λέξη της ταιριάζει.',
    dictionaryGameStart: 'Παίξε!',
    myDictionaryTitle: 'Λεξικό μου',
  },

  // Grammar rules game
  grammar: {
    chooseRule: 'Διάλεξε κανόνα',
    noRulesAvailable: 'Δεν υπάρχουν διαθέσιμοι κανόνες για αυτή την τάξη.',
    ruleIntroTitle: 'Ο Κανόνας',
    tipsLabel: 'Συμβουλές',
    startExercises: 'Ξεκίνα τις ασκήσεις',
    exerciseLabel: 'Άσκηση',
    correctAnswer: 'Σωστή απάντηση:',
    tipLabel: 'Συμβουλή',
    completed: 'Ολοκλήρωσες τον κανόνα!',
    score: 'Σκορ',
    tryAnother: 'Δοκίμασε άλλο κανόνα',
    currentGradeSection: 'Κανόνες της τάξης σου',
    previousGradeSection: 'Επανάληψη από {grade}',
    previousGradeBadge: 'από {grade}',
  },

  // Session page
  session: {
    question: 'Ερώτηση',
    of: 'από',
    correct: 'Σωστό!',
    almost: 'Σχεδόν! Δοκίμασε ξανά.',
    nice: 'Μπράβο!',
    excellent: 'Τέλεια!',
    keepGoing: 'Συνέχισε έτσι!',
    hint: 'Βοήθεια',
    showHint: 'Δείξε βοήθεια',
    typeAnswer: 'Πληκτρολόγησε την απάντησή σου',
    chooseAnswer: 'Διάλεξε τη σωστή απάντηση',
    fillBlanks: 'Συμπλήρωσε τα κενά',
    findMistake: 'Πάτησε τη λέξη με το λάθος',
    correctWord: 'Η σωστή λέξη είναι:',
    correctSpelling: 'Η σωστή γραφή είναι:',
    // Shown when the child taps a word that is actually correctly spelled
    // in a FindTheMistake exercise. `{word}` is interpolated.
    wordIsCorrectMistakeElsewhere:
      'Η λέξη «{word}» είναι σωστή. Για δες καλύτερα, το λάθος βρίσκεται αλλού.',
    attempt: 'Προσπάθεια',
    ruleLabel: 'Κανόνας',
    explanationLabel: 'Εξήγηση',
    relatedRulesLabel: 'Σχετικοί κανόνες',
    didntMakeIt: 'Δεν πειράζει! Δες τη σωστή απάντηση:',
    practiceLabel: 'Άσκηση',
    mistakesLabel: 'Επανάληψη λαθών',
    reviewLabel: 'Αξιολόγηση',
    reviewIntroTitle: 'Ώρα για επανάληψη!',
    reviewIntroText: 'Ας δούμε τι έμαθες. Θα δεις πάλι τις ίδιες 10 λέξεις σαν κουίζ — μία προσπάθεια ανά λέξη.',
    startReview: 'Ξεκίνα την αξιολόγηση',
    historyLabel: 'Επανάληψη όλων',
    historyEmptyTitle: 'Δεν έχεις παίξει ακόμη',
    historyEmptyText:
      'Παίξε λίγο πρώτα για να μαζευτούν λέξεις για επανάληψη και ξαναέλα εδώ.',
    focusedLabel: 'Εστιασμένη εξάσκηση',
    focusedEmptyTitle: 'Δεν βρέθηκαν ασκήσεις',
    focusedEmptyText:
      'Δεν υπάρχουν διαθέσιμες ασκήσεις γι’ αυτό το σημείο αυτή τη στιγμή. Δοκίμασε το κανονικό κουίζ.',
  },

  // Dedicated make-up page (tap on a missed past day in the calendar).
  makeup: {
    bannerLabel: 'Αναπλήρωση',
    pageTitleTemplate: 'Αναπλήρωση {label}',
    intro:
      'Ολοκλήρωσε τις αποστολές της ημέρας για να την κάνεις πράσινη στο ημερολόγιο.',
    completedTitle: 'Ολοκληρώθηκε!',
    completedBody:
      'Έκανες όλες τις αποστολές αυτής της ημέρας. Η ημέρα πέρασε στο ημερολόγιό σου.',
    questPanelTitle: 'Αποστολές της ημέρας',
    progressLabel: '{progress} από {target}',
    noGradeHint:
      'Διάλεξε πρώτα τάξη από την αρχική για να ξεκινήσεις την αναπλήρωση.',
    backToCalendar: '📅 Πίσω στο ημερολόγιο',
    notFoundTitle: 'Δεν βρέθηκαν δεδομένα',
    notFoundBody:
      'Δεν έχει αποθηκευτεί ιστορικό για αυτή την ημέρα ακόμη.',
  },

  // Personalised home-page CTA that surfaces the child's top weakness.
  weaknessCta: {
    title: 'Δυσκολεύεσαι λίγο εδώ',
    bodyTemplate:
      'Είδα ότι μπερδεύεσαι στο «{label}». Πάμε για 10 ερωτήσεις εστιασμένες σε αυτό;',
    cta: '▶️ Ξεκίνα εστιασμένη εξάσκηση',
  },

  // Session complete
  complete: {
    title: 'Μπράβο! Τελείωσες!',
    perfect: 'Τέλειο σκορ!',
    score: 'Σκορ',
    xpEarned: 'Πόντοι που κέρδισες',
    correctAnswers: 'Σωστές απαντήσεις',
    practiceScore: 'Άσκηση',
    reviewScore: 'Αξιολόγηση',
    streakDays: 'Ημέρες στη σειρά',
    newBadge: 'Νέο παράσημο!',
    playAgain: 'Παίξε ξανά',
    backHome: 'Επιστροφή',
  },

  // Medals (dynamic — count rises automatically as training days complete)
  medals: {
    heading: 'Μετάλλια',
    daily: 'Ημερήσιο',
    weekly: 'Εβδομαδιαίο',
    monthly: 'Μηνιαίο',
  },

  // Progress page
  progress: {
    title: 'Η πρόοδός σου',
    totalXp: 'Σύνολο πόντων',
    currentStreak: 'Σειρά ημερών',
    bestStreak: 'Καλύτερη σειρά',
    badgesEarned: 'Παράσημα',
    weaknesses: 'Σημεία προς βελτίωση',
    strengths: 'Δυνατά σημεία',
    noBadgesYet: 'Δεν έχεις κερδίσει παράσημα ακόμα. Συνέχισε να εξασκείσαι!',
    sessionsCompleted: 'Ολοκληρωμένες εξασκήσεις',
    nextBadges: 'Επόμενα παράσημα',
    openBadgeAria: 'Δες λεπτομέρειες για το παράσημο {label}',
  },

  // Badge detail modal (opens when child taps any badge on the progress page)
  badgeModal: {
    earnedTitle: 'Κερδισμένο!',
    earnedOn: 'Κερδισμένο στις {date}',
    earnedLegacy: 'Το έχεις κερδίσει',
    lockedTitle: 'Πώς θα το κερδίσεις',
    progressLabel: 'Πρόοδος',
    progressTemplate: '{current} / {target}',
    remainingTemplate: 'Σου λείπουν ακόμα {remaining}',
    completeTemplate: 'Είσαι ένα βήμα πριν το ξεκλειδώσεις!',
    close: 'Κλείσιμο',
    // Tip templates per condition kind. `{n}` = target, `{remaining}` = how
    // many more the child needs. Keep the tone encouraging and concrete.
    tips: {
      xp: 'Μάζεψε {n} πόντους εμπειρίας παίζοντας ασκήσεις και ολοκληρώνοντας αποστολές.',
      sessions: 'Ολοκλήρωσε {n} εξασκήσεις στο σύνολο. Κάθε ολοκληρωμένη εξάσκηση μετράει!',
      streak: 'Παίξε τουλάχιστον μία εξάσκηση κάθε μέρα για {n} συνεχόμενες μέρες.',
      perfect_sessions: 'Κάνε {n} εξασκήσεις χωρίς κανένα λάθος (10 στα 10).',
      correct_streak: 'Πέτυχε {n} σωστές απαντήσεις στη σειρά με την πρώτη προσπάθεια.',
      reviews: 'Ολοκλήρωσε {n} φάσεις αξιολόγησης (το δεύτερο μέρος κάθε εξάσκησης).',
      quests_completed: 'Ολοκλήρωσε {n} ημερήσιες αποστολές συνολικά.',
      trained_days: 'Ολοκλήρωσε όλες τις ημερήσιες αποστολές σε {n} διαφορετικές ημέρες.',
      weakness_improved: 'Βελτίωσε {n} σημεία αδυναμίας σου παίζοντας στοχευμένες εξασκήσεις.',
      mastered_items: 'Μάθε καλά {n} λέξεις — απάντησε τις σωστά συνέχεια για να φύγουν από τα λάθη.',
      badges_earned: 'Συγκέντρωσε {n} παράσημα συνολικά. Κάθε νέο παράσημο σε φέρνει πιο κοντά!',
      // Per-exercise-type variants — the noun changes ώστε να ταιριάζει με
      // τον τύπο της άσκησης.
      exercise_type_fillTheBlank: 'Συμπλήρωσε σωστά {n} κενά σε ασκήσεις τύπου «συμπλήρωση».',
      exercise_type_dictation: 'Γράψε σωστά {n} λέξεις σε ασκήσεις υπαγόρευσης.',
      exercise_type_multipleChoice: 'Διάλεξε σωστά {n} φορές σε ασκήσεις πολλαπλής επιλογής.',
      exercise_type_findTheMistake: 'Εντόπισε {n} κρυμμένα λάθη σε ασκήσεις «βρες το λάθος».',
      // Fallback when we can't parse the condition (should not happen with
      // current badges, but keeps the UI graceful for future ones).
      generic: 'Συνέχισε να εξασκείσαι και θα το ξεκλειδώσεις σύντομα!',
    },
  },

  // Training calendar (Progress page)
  calendar: {
    title: 'Ημερολόγιο εκπαίδευσης',
    subtitle: 'Ημέρες όπου ολοκλήρωσες όλες τις αποστολές',
    monthlyCount: 'Αυτόν τον μήνα: {count}',
    totalCount: 'Σύνολο: {count} ημέρες',
    progressToReward: '{count} / 30 ημέρες για το παράσημο',
    prevMonth: 'Προηγούμενος μήνας',
    nextMonth: 'Επόμενος μήνας',
    todayLabel: 'Σήμερα',
    completedAria: 'Ολοκληρωμένη ημέρα εκπαίδευσης',
    makeupAria: 'Αναπλήρωσε την ημέρα {day}',
    makeupTitle: 'Πάτησε για αναπλήρωση',
    makeupBannerTemplate: '⏪ Αναπλήρωση για {label}',
    // Full weekday names indexed by JS getDay() (0 = Sun … 6 = Sat).
    weekdaysFull: [
      'Κυριακή',
      'Δευτέρα',
      'Τρίτη',
      'Τετάρτη',
      'Πέμπτη',
      'Παρασκευή',
      'Σάββατο',
    ],
    months: [
      'Ιανουάριος',
      'Φεβρουάριος',
      'Μάρτιος',
      'Απρίλιος',
      'Μάιος',
      'Ιούνιος',
      'Ιούλιος',
      'Αύγουστος',
      'Σεπτέμβριος',
      'Οκτώβριος',
      'Νοέμβριος',
      'Δεκέμβριος',
    ],
    // Monday-first weekday short labels.
    weekdays: ['Δε', 'Τρ', 'Τε', 'Πε', 'Πα', 'Σα', 'Κυ'],
  },

  // Error tag labels (for progress UI)
  errorLabels: {
    tonismos: 'Τονισμός',
    ei_i_oi: 'ει / η / οι / υ',
    dipla_sygfona: 'Διπλά σύμφωνα',
    afonos_h: 'Άφωνο η',
    omikron_omega: 'ο / ω',
    epsilon_aita: 'ε / αι',
    upsilon_oi: 'υ / οι',
    ou_oi_diphthong: 'ου / οι',
    gender_agreement: 'Συμφωνία γένους',
    verb_ending: 'Καταλήξεις ρημάτων',
    compound_words: 'Σύνθετες λέξεις',
    spelling_attention: 'Πρόσεξε κάθε γράμμα',
  },

  // Encouragement messages (rotating)
  encouragement: {
    correct: ['Μπράβο!', 'Τέλεια!', 'Εξαιρετικά!', 'Πολύ καλά!', 'Φοβερό!'],
    wrong: [
      'Σχεδόν! Δοκίμασε ξανά.',
      'Λίγο ακόμα, συνέχισε!',
      'Δεν πειράζει, ξαναπροσπάθησε.',
      'Κοντά ήσουν! Δοκίμασε ξανά.',
    ],
  },

  // Errors / fallbacks
  errors: {
    noContent: 'Δεν υπάρχει διαθέσιμο περιεχόμενο.',
    loading: 'Φόρτωση…',
    sessionEmpty: 'Δεν βρέθηκαν ασκήσεις για αυτό το επίπεδο.',
  },

  // Confirmation modal shown when the child tries to leave a session.
  // Reinforces the pending-vs-committed XP rule: nothing they did this
  // session counts unless they finish the assessment too.
  exitGuard: {
    title: 'Σίγουρα θες να φύγεις;',
    message:
      'Αν φύγεις τώρα, οι πόντοι που μάζεψες σε αυτή την άσκηση δεν θα μετρήσουν. Χρειάζεται να τελειώσεις και την αξιολόγηση για να τους πάρεις.',
    messageShort:
      'Αν φύγεις τώρα, οι πόντοι αυτής της άσκησης δεν θα μετρήσουν.',
    confirm: 'Έξοδος χωρίς πόντους',
    cancel: 'Συνέχισε την άσκηση',
  },

  // Difficulty labels
  difficulty: {
    1: 'Εύκολο',
    2: 'Μέτριο',
    3: 'Δύσκολο',
  },

  // Contextual hints per error_tag, shown after a wrong attempt (no answer reveal).
  // `first` is shown after the 1st wrong attempt, `second` after the 2nd (more specific).
  // NOTE: draft content — pending linguistic review by a Greek primary-school educator.
  attemptHints: {
    first: {
      tonismos: 'Πρόσεξε τον τόνο της λέξης. Σε ποια συλλαβή τονίζεται;',
      ei_i_oi: 'Σκέψου ξανά: γράφεται με «ει», «η», «οι» ή «υ»;',
      dipla_sygfona: 'Μήπως χρειάζονται διπλά σύμφωνα κάπου;',
      afonos_h: 'Πρόσεξε αν χρειάζεται «η» στο τέλος της λέξης.',
      omikron_omega: 'Γράφεται με «ο» ή με «ω»;',
      epsilon_aita: 'Γράφεται με «ε» ή με «αι»;',
      upsilon_oi: 'Γράφεται με «υ» ή με «οι»;',
      ou_oi_diphthong: 'Πες δυνατά τη λέξη: ακούς /ου/ ή /ι/;',
      gender_agreement: 'Πρόσεξε το γένος της λέξης.',
      verb_ending: 'Σκέψου ξανά την κατάληξη του ρήματος.',
      compound_words: 'Πρόσεξε πώς ενώνονται τα δύο μέρη της λέξης.',
      generic: 'Σχεδόν το βρήκες! Σκέψου το λίγο ακόμα.',
    },
    second: {
      tonismos: 'Πες δυνατά τη λέξη και άκου ποια συλλαβή ακούγεται πιο δυνατά — εκεί μπαίνει ο τόνος.',
      ei_i_oi: 'Σκέψου παρόμοιες λέξεις που ξέρεις. Με ποιο γράφονται στη λήγουσα;',
      dipla_sygfona: 'Σε αυτή τη λέξη υπάρχει σύμφωνο που γράφεται διπλό. Μάθαμε ότι κάποιες λέξεις θέλουν διπλό γράμμα.',
      afonos_h: 'Στη λέξη αυτή υπάρχει ένα γράμμα που γράφεται αλλά δεν ακούγεται καθαρά.',
      omikron_omega: 'Σκέψου: στις καταλήξεις των ρημάτων στον ενεστώτα γράφουμε «ω» (π.χ. «γράφω»).',
      epsilon_aita: 'Σκέψου άλλες λέξεις από την ίδια ρίζα. Πώς γράφονται;',
      upsilon_oi: 'Σκέψου παρόμοιες λέξεις. Γράφονται με «υ» ή με «οι»;',
      ou_oi_diphthong: 'Το «ου» ακούγεται /ου/ και το «οι» ακούγεται /ι/. Άκουσε ξανά τον ήχο της λέξης.',
      gender_agreement: 'Έλεγξε αν το άρθρο και το επίθετο ταιριάζουν στο γένος με το ουσιαστικό.',
      verb_ending: 'Σκέψου ποιο πρόσωπο και χρόνο γράφεις. Η κατάληξη αλλάζει ανάλογα.',
      compound_words: 'Χώρισέ την νοερά στα δύο μέρη της. Πώς γράφεται το καθένα μόνο του;',
      generic: 'Είσαι κοντά! Δοκίμασε με προσοχή ακόμα μία φορά.',
    },
  },

  // Brief grammar rule explanations per error_tag, shown after the final wrong attempt.
  // NOTE: draft content — pending linguistic review by a Greek primary-school educator.
  ruleExplanations: {
    tonismos:
      'Κάθε λέξη με δύο ή περισσότερες συλλαβές παίρνει τόνο στη συλλαβή που τονίζεται όταν την προφέρουμε.',
    ei_i_oi:
      'Ο ήχος «ι» γράφεται με «ι», «η», «υ», «ει» ή «οι». Κάθε λέξη γράφεται με συγκεκριμένο τρόπο που τη μαθαίνουμε ορθογραφικά.',
    dipla_sygfona:
      'Μερικές λέξεις γράφονται με διπλά σύμφωνα (π.χ. «θάλασσα», «άλλος», «γράμμα»). Δεν ακούγονται διπλά, αλλά γράφονται.',
    afonos_h:
      'Σε ορισμένες λέξεις υπάρχει γράμμα που γράφεται αλλά δεν ακούγεται καθαρά. Μαθαίνουμε αυτές τις λέξεις με προσοχή.',
    omikron_omega:
      'Ο ήχος «ο» γράφεται με «ο» ή με «ω». Στις καταλήξεις των ρημάτων του ενεστώτα γράφουμε «ω» (π.χ. «γράφω»).',
    epsilon_aita:
      'Ο ήχος «ε» γράφεται με «ε» ή με «αι». Κάθε λέξη έχει τη δική της ορθογραφία που μαθαίνουμε.',
    upsilon_oi:
      'Ο ήχος «ι» γράφεται μερικές φορές με «υ» ή με «οι». Πρέπει να μάθουμε ορθογραφικά κάθε λέξη.',
    ou_oi_diphthong:
      'Το δίφθογγο «ου» ακούγεται /ου/ — εντελώς διαφορετικός ήχος από το «οι» που ακούγεται /ι/. Αν αλλάξουμε «ου» σε «οι», αλλάζει ο ήχος της λέξης.',
    gender_agreement:
      'Το άρθρο και το επίθετο πρέπει πάντα να συμφωνούν με το ουσιαστικό στο γένος (αρσενικό, θηλυκό, ουδέτερο) και στον αριθμό.',
    verb_ending:
      'Τα ρήματα αλλάζουν κατάληξη ανάλογα με το πρόσωπο και τον χρόνο. Στον ενεστώτα στο α΄ πρόσωπο τελειώνουν συνήθως σε «-ω».',
    compound_words:
      'Οι σύνθετες λέξεις σχηματίζονται από δύο μέρη. Συχνά το πρώτο μέρος αλλάζει λίγο όταν ενώνεται με το δεύτερο.',
    generic:
      'Συνέχισε να εξασκείσαι! Με την πρακτική θα μάθεις την ορθογραφία αυτής της λέξης.',
  },
};

/**
 * Pick the most relevant contextual hint based on error tags + attempt number.
 * `attemptNumber` is the attempt that just failed (1 or 2 for non-final).
 */
export function getAttemptHint(errorTags = [], attemptNumber = 1) {
  const pool = attemptNumber >= 2 ? t.attemptHints.second : t.attemptHints.first;
  for (const tag of errorTags) {
    if (pool[tag]) return pool[tag];
  }
  return pool.generic;
}

/**
 * Pick the most relevant grammar rule explanation based on error tags.
 */
export function getRuleExplanation(errorTags = []) {
  for (const tag of errorTags) {
    if (t.ruleExplanations[tag]) return t.ruleExplanations[tag];
  }
  return t.ruleExplanations.generic;
}

/**
 * Build the contextual hint shown when the child taps a correctly-spelled
 * word in FindTheMistake. Interpolates the selected word into the template.
 */
export function getCorrectWordHint(selectedWord) {
  const safe =
    typeof selectedWord === 'string' && selectedWord.length > 0
      ? selectedWord
      : '';
  return t.session.wordIsCorrectMistakeElsewhere.replace('{word}', safe);
}

/**
 * Render a daily-quest label, interpolating `{n}` and `{wordForm}` for
 * dynamic quests. Falls back gracefully to the raw label key when no entry
 * exists (e.g. stale serialized quest from an older build).
 *
 * The renderer is intentionally simple — only the placeholders the quest
 * pool actually uses are interpolated. Unknown placeholders are left intact.
 */
export function renderQuestLabel(quest) {
  if (!quest) return '';
  const raw = t.quests.labels[quest.labelKey] ?? quest.labelKey ?? '';
  const vars = quest.labelVars ?? {};
  const n = Number.isFinite(vars.n) ? vars.n : quest.target;
  const wordForm =
    n === 1 ? t.quests.wordForms.singularLexi : t.quests.wordForms.pluralLexi;
  return String(raw)
    .replace(/\{n\}/g, String(n ?? ''))
    .replace(/\{wordForm\}/g, wordForm);
}

/**
 * Render a grammar rule's `feedback_template` strings by substituting
 * the supported placeholders. Unknown placeholders are left intact as
 * plain text so the app never crashes on unexpected template content.
 *
 * Supported placeholders:
 *   {answer}  → vars.answer
 *   {title}   → vars.title
 *   {wrong}   → vars.wrong    (the child's wrong grapheme(s); dynamic rule)
 *   {correct} → vars.correct  (the correct grapheme(s); dynamic rule)
 *
 * A template field that REQUIRES {wrong} or {correct} but is rendered
 * without those vars yields null — letting RuleTipPanel fall back to the
 * rule's static tips list instead of printing an empty «» quote.
 *
 * Returns: { short: string | null, learning: string | null }
 * — null when the corresponding template field is missing, empty, OR is
 * gated by a missing dynamic var.
 */
export function renderFeedbackTemplate(template, vars = {}) {
  if (!template || typeof template !== 'object') {
    return { short: null, learning: null };
  }
  const interpolate = (str) => {
    if (typeof str !== 'string' || str.length === 0) return null;
    if (str.includes('{wrong}') && !vars.wrong) return null;
    if (str.includes('{correct}') && !vars.correct) return null;
    return str
      .replace(/\{answer\}/g, vars.answer ?? '')
      .replace(/\{title\}/g, vars.title ?? '')
      .replace(/\{wrong\}/g, vars.wrong ?? '')
      .replace(/\{correct\}/g, vars.correct ?? '');
  };
  return {
    short: interpolate(template.short),
    learning: interpolate(template.learning),
  };
}

export default t;
